import { useState, useEffect, useCallback, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Megaphone,
  Lock,
  TrendingUp,
  MessageCircle,
  DollarSign,
  Sparkles,
  BarChart3,
  Pencil,
  Zap,
  Clock,
  Send,
  ShoppingBag,
  ArrowRight,
  Plus,
  Gift,
  RotateCcw,
  CheckCircle2,
  Lightbulb,
  Eye,
  ShoppingCart,
  Cake,
  User,
  Users,
  Filter,
  X,
} from "lucide-react";
import CampaignReportModal from "../components/modals/CampaignReportModal";
import CampaignMessagesModal from "../components/modals/CampaignMessagesModal";
import CouponRouletteConfigModal from "../components/modals/CouponRouletteConfigModal";
import { CarregandoGrade } from "../components/carregando";
import { ReviewCampaignConfigModal } from "../features/campanha-avaliacao/components/ReviewCampaignConfigModal";
import { ROTA_CONFIG, ROTA_RELATORIO } from "../features/ofertas-do-dia/admin/rotas";
import { ROTA_CONFIG_RECUPERADOR, ROTA_RELATORIO_RECUPERADOR } from "../features/recuperador/rotas";
import { ROTA_RELATORIO_SUGESTOES } from "../features/sugestoes/admin/rotas";

const API = "/api";
const getAuthHeader = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
});

const formatCurrency = (v) =>
  `R$ ${Number(v || 0).toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const compactNumber = (v) => {
  const n = Number(v || 0);
  if (n >= 1000) return `${(n / 1000).toFixed(1).replace(".0", "").replace(".", ",")}k`;
  return String(n);
};

// Porcentagem com vírgula (12,5%), como todo número do painel.
const formatPct = (parte, total) => `${(total > 0 ? (parte / total) * 100 : 0).toFixed(1).replace(".", ",")}%`;

// Carregamento com cards (25/10/2026): mesma grade e altura dos cards de campanha.
const GRADE_CAMPANHAS = "grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4";
// `soltoNaGrade`: um card só, já dentro da grade da página (card da Avaliação).
const CardsCampanhaCarregando = ({ quantos, soltoNaGrade = false }) => (
  <CarregandoGrade quantos={quantos} className={soltoNaGrade ? "" : GRADE_CAMPANHAS} altura="h-[430px]" />
);

const CAMPAIGN_TYPES = {
  sales_recovery: {
    label: "Recuperador de Vendas",
    icon: Zap,
    tagline: "A CLEO chama de volta quem parou no meio do pedido",
    stage: "beta",
    description:
      "Quando o cliente conversa no WhatsApp e para sem pedir, a CLEO espera ele sair do cardápio, confere se faz sentido chamar e escreve uma mensagem só pra ele — com o carrinho, o último pedido e as promoções da loja.",
    flow: [
      { icon: Send, label: "Cardápio" },
      { icon: Clock, label: "Parou" },
      { icon: MessageCircle, label: "CLEO escreve", highlight: true },
      { icon: ShoppingBag, label: "Pedido" },
    ],
    metrics: {
      triggered: { label: "Mensagens", accent: "text-slate-50" },
      converted: { label: "Pedidos", accent: "text-green-400" },
      revenue: { label: "Receita", accent: "text-emerald-400" },
      rate: { label: "Conversão", accent: "text-purple-400" },
    },
  },
  cross_sell: {
    label: "Sugestões Inteligentes",
    icon: Lightbulb,
    tagline: "O que combina com o carrinho de cada cliente",
    stage: "beta",
    description:
      "Aprende com os seus pedidos o que costuma sair junto e sugere, pra cada carrinho, o que combina — na sacola, logo depois de adicionar e antes de finalizar. Nunca repete o que já está no carrinho e prioriza as ofertas do cliente. Sem configuração — basta ligar.",
    flow: [
      { icon: ShoppingCart, label: "Carrinho" },
      { icon: TrendingUp, label: "Combina", highlight: true },
      { icon: Plus, label: "Adicionar" },
      { icon: DollarSign, label: "+Ticket" },
    ],
    metrics: {
      triggered: { label: "Vezes exibido", accent: "text-slate-50" },
      converted: { label: "Adicionados", accent: "text-green-400" },
      revenue: { label: "Receita extra", accent: "text-emerald-400" },
      rate: { label: "Conversão", accent: "text-purple-400" },
    },
  },
  daily_offers: {
    label: "Campanha Inteligente",
    icon: Sparkles,
    tagline: "Cada cliente caça as ofertas que são só dele",
    stage: "beta",
    description:
      "Todo dia cada cliente cadastrado encontra, espalhadas pelo cardápio digital, ofertas sorteadas só pra ele dentro do desconto máximo que você define. Quem compara com a família e os amigos cria conta pra ver as suas.",
    flow: [
      { icon: User, label: "Cadastro" },
      { icon: Sparkles, label: "Sorteio", highlight: true },
      { icon: Eye, label: "Caça" },
      { icon: ShoppingBag, label: "Pedido" },
    ],
    metrics: {
      triggered: { label: "Ofertas sorteadas", accent: "text-slate-50" },
      converted: { label: "Pedidos com oferta", accent: "text-green-400" },
      revenue: { label: "Faturado", accent: "text-emerald-400" },
      rate: { label: "Pedidos por oferta", accent: "text-purple-400" },
    },
  },
};

// Stages das campanhas — exibidos como badge ao lado do "SISTEMA"
const STAGE_BADGES = {
  beta: {
    label: "BETA",
    className: "bg-amber-500/20 text-amber-300 border border-amber-500/30",
    title: "Em beta — funcionando, sujeito a ajustes",
  },
  dev: {
    label: "EM DESENVOLVIMENTO",
    className: "bg-rose-500/20 text-rose-300 border border-rose-500/30",
    title: "Em desenvolvimento — ainda não disponível",
  },
};

export default function Campanhas() {
  const [campaigns, setCampaigns] = useState([]);
  // Carregamento com cards só na primeira abertura; ligar/desligar recarrega sem piscar.
  const [primeiraCarga, setPrimeiraCarga] = useState(true);
  const [reportCampaign, setReportCampaign] = useState(null);
  const [messagesCampaign, setMessagesCampaign] = useState(null);
  const [rouletteCampaign, setRouletteCampaign] = useState(null);
  // Campanha Inteligente tem páginas próprias de configuração e relatório.
  const navigate = useNavigate();

  const fetchCampaigns = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/campaigns`, getAuthHeader());
      setCampaigns(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      if (err?.response?.status !== 403) {
        toast.error("Erro ao carregar campanhas");
      }
      setCampaigns([]);
    } finally {
      setPrimeiraCarga(false);
    }
  }, []);

  useEffect(() => {
    fetchCampaigns();
  }, [fetchCampaigns]);

  const toggleActive = async (campaign) => {
    try {
      await axios.put(
        `${API}/campaigns/${campaign.id}`,
        { is_active: !campaign.is_active },
        getAuthHeader()
      );
      toast.success(
        !campaign.is_active ? "Campanha ativada" : "Campanha desativada"
      );
      fetchCampaigns();
    } catch {
      toast.error("Erro ao atualizar campanha");
    }
  };

  const handleSaveMessages = async (messages) => {
    if (!messagesCampaign) return;
    try {
      const newConfig = { ...(messagesCampaign.config || {}), messages };
      await axios.put(
        `${API}/campaigns/${messagesCampaign.id}`,
        { config: newConfig },
        getAuthHeader()
      );
      toast.success("Mensagens atualizadas");
      setMessagesCampaign(null);
      fetchCampaigns();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Erro ao salvar mensagens";
      toast.error(msg);
    }
  };

  const handleSaveRouletteConfig = async (newConfig) => {
    if (!rouletteCampaign) return;
    try {
      await axios.put(
        `${API}/campaigns/${rouletteCampaign.id}`,
        { config: newConfig },
        getAuthHeader()
      );
      toast.success("Configuração dos cupons atualizada");
      setRouletteCampaign(null);
      fetchCampaigns();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Erro ao salvar configuração";
      toast.error(msg);
    }
  };

  const systemCampaigns = campaigns.filter((c) => c.is_system);
  const customCampaigns = campaigns.filter((c) => !c.is_system);

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-1">
            <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-purple-500 to-fuchsia-600 flex items-center justify-center shadow-lg shadow-purple-500/30">
              <Megaphone className="w-5 h-5 text-white" strokeWidth={2.2} />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">
                Campanhas
              </h1>
              <p className="text-muted-foreground text-sm">
                Automatize interações e recupere vendas no piloto automático
              </p>
            </div>
          </div>
        </div>

        {/* Grid único: sistema + customizadas juntas, até 3 por linha */}
        {primeiraCarga ? <CardsCampanhaCarregando quantos={4} /> : (
        <div className={GRADE_CAMPANHAS}>
          {systemCampaigns.map((campaign) => {
            const ofertas = campaign.type === 'daily_offers';
            const recuperador = campaign.type === 'sales_recovery';
            const editProps = campaign.type === 'cross_sell'
              ? { onEditMessages: undefined, editLabel: undefined }
              : ofertas
                ? { onEditMessages: () => navigate(ROTA_CONFIG), editLabel: 'Configurar' }
                : recuperador
                  ? { onEditMessages: () => navigate(ROTA_CONFIG_RECUPERADOR), editLabel: 'Configurar' }
                  : { onEditMessages: () => setMessagesCampaign(campaign), editLabel: 'Mensagens' };
            const paginaRelatorio = ofertas ? ROTA_RELATORIO : recuperador ? ROTA_RELATORIO_RECUPERADOR
              : campaign.type === 'cross_sell' ? ROTA_RELATORIO_SUGESTOES : null;
            return (
              <SystemCampaignCard
                key={campaign.id}
                campaign={campaign}
                onToggle={() => toggleActive(campaign)}
                onReport={() => (paginaRelatorio ? navigate(paginaRelatorio) : setReportCampaign(campaign))}
                {...editProps}
              />
            );
          })}
          {customCampaigns.map((campaign) => (
            <CampaignCard
              key={campaign.id}
              campaign={campaign}
              onToggle={() => toggleActive(campaign)}
              onReport={() => setReportCampaign(campaign)}
              onEditMessages={() => setMessagesCampaign(campaign)}
            />
          ))}
          {/* Campanha de Avaliação — agora no grid junto com as outras */}
          <ReviewCampaignSection />
          {/* v1.5.467: os placeholders EM BREVE de Aniversário e Disparo em
              Massa saíram a pedido do dono — disparos agora têm aba própria
              (Impulsione → Disparos em massa) e o aniversário volta quando for
              funcional. As specs seguem em docs/features/. */}
        </div>
        )}

      </div>

      {reportCampaign && (
        <CampaignReportModal
          campaignId={reportCampaign.id}
          onClose={() => setReportCampaign(null)}
        />
      )}

      {messagesCampaign && (
        <CampaignMessagesModal
          campaign={messagesCampaign}
          onClose={() => setMessagesCampaign(null)}
          onSave={handleSaveMessages}
        />
      )}

      {rouletteCampaign && (
        <CouponRouletteConfigModal
          campaign={rouletteCampaign}
          onClose={() => setRouletteCampaign(null)}
          onSave={handleSaveRouletteConfig}
        />
      )}
    </div>
  );
}

/* ========================================================================= */
/* SYSTEM CAMPAIGN CARD — compacto, cor sólida, cabe 3 por linha             */
/* ========================================================================= */

function SystemCampaignCard({ campaign, onToggle, onReport, onEditMessages, editLabel = "Mensagens" }) {
  const meta = CAMPAIGN_TYPES[campaign.type] || CAMPAIGN_TYPES.sales_recovery;
  const Icon = meta.icon;
  const triggered = Number(campaign.triggered_count || 0);
  const converted = Number(campaign.converted_count || 0);
  const revenue = Number(campaign.recovered_revenue || 0);
  const isActive = !!campaign.is_active;

  return (
    <div
      className={`relative rounded-2xl border border-purple-500/25 bg-[#1a0b2e] text-slate-50 overflow-hidden transition-all hover:border-purple-500/40 flex flex-col ${
        !isActive ? "opacity-90" : ""
      }`}
    >
      {/* Top stripe */}
      <div className="h-1 bg-purple-500/70" />

      <div className="p-4 flex flex-col flex-1">
        {/* Header */}
        <div className="flex items-start justify-between gap-2 mb-3">
          <div className="flex items-start gap-3 min-w-0 flex-1">
            <div className="relative flex-shrink-0">
              <div className="w-10 h-10 rounded-xl bg-purple-600 flex items-center justify-center">
                <Icon className="w-5 h-5 text-white" strokeWidth={2.2} />
              </div>
              {isActive && (
                <span className="absolute -top-0.5 -right-0.5 flex h-2.5 w-2.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500 border border-[#1a0b2e]" />
                </span>
              )}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5 flex-wrap">
                <h3 className="font-bold text-sm truncate">{campaign.name}</h3>
                <span
                  className="inline-flex items-center gap-0.5 text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-purple-500/20 text-purple-300 border border-purple-500/30"
                  title="Campanha de sistema — não pode ser excluída"
                >
                  <Lock className="w-2.5 h-2.5" /> SISTEMA
                </span>
                {meta.stage && STAGE_BADGES[meta.stage] && (
                  <span
                    className={`inline-flex items-center text-[9px] font-bold px-1.5 py-0.5 rounded-full ${STAGE_BADGES[meta.stage].className}`}
                    title={STAGE_BADGES[meta.stage].title}
                  >
                    {STAGE_BADGES[meta.stage].label}
                  </span>
                )}
              </div>
              <p className="text-[11px] text-purple-300/80 font-medium mt-0.5 line-clamp-1">
                {meta.tagline}
              </p>
            </div>
          </div>
          <CompactToggle active={isActive} onChange={onToggle} />
        </div>

        {/* Descrição */}
        <p className="text-xs text-slate-400 mb-3 line-clamp-2">
          {campaign.description || meta.description}
        </p>

        {/* Flow compacto: ícones em linha — definido em meta.flow por tipo de campanha */}
        <div className="mb-3 rounded-xl bg-black/30 border border-white/5 p-2.5">
          <p className="text-[9px] uppercase tracking-widest font-bold text-purple-400/80 mb-2">
            Como funciona
          </p>
          <div className="flex items-center gap-1">
            {(meta.flow || []).map((step, idx, arr) => (
              <Fragment key={idx}>
                <CompactStep icon={step.icon} label={step.label} highlight={step.highlight} />
                {idx < arr.length - 1 && <CompactArrow />}
              </Fragment>
            ))}
          </div>
        </div>

        {/* Métricas 2x2 — labels personalizados por tipo de campanha */}
        <div className="grid grid-cols-2 gap-2 mb-3">
          <MetricBlock
            label={meta.metrics?.triggered?.label || "Disparos"}
            value={compactNumber(triggered)}
            accent={meta.metrics?.triggered?.accent || "text-slate-50"}
          />
          <MetricBlock
            label={meta.metrics?.converted?.label || "Conversões"}
            value={compactNumber(converted)}
            accent={meta.metrics?.converted?.accent || "text-green-400"}
          />
          <MetricBlock
            label={meta.metrics?.revenue?.label || "Receita"}
            value={formatCurrency(revenue)}
            accent={meta.metrics?.revenue?.accent || "text-emerald-400"}
          />
          <MetricBlock
            label={meta.metrics?.rate?.label || "Conversão"}
            value={formatPct(converted, triggered)}
            accent={meta.metrics?.rate?.accent || "text-purple-400"}
          />
        </div>

        {/* Ações */}
        <div className="flex items-center gap-1.5 mt-auto">
          <button
            onClick={onReport}
            className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10 text-xs font-semibold transition-colors"
          >
            <BarChart3 className="w-3.5 h-3.5" /> Relatório
          </button>
          {onEditMessages && (
            <button
              onClick={onEditMessages}
              className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10 text-xs font-semibold transition-colors"
            >
              <Pencil className="w-3.5 h-3.5" /> {editLabel}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function CompactStep({ icon: Icon, label, highlight }) {
  return (
    <div
      className={`flex-1 flex flex-col items-center gap-0.5 py-1.5 px-1 rounded-md ${
        highlight ? "bg-purple-500/20 border border-purple-500/30" : ""
      }`}
    >
      <Icon
        className={`w-3.5 h-3.5 ${
          highlight ? "text-purple-300" : "text-slate-400"
        }`}
        strokeWidth={2}
      />
      <span
        className={`text-[9px] font-semibold ${
          highlight ? "text-purple-200" : "text-slate-400"
        }`}
      >
        {label}
      </span>
    </div>
  );
}

function CompactArrow() {
  return <ArrowRight className="w-3 h-3 text-purple-500/40 flex-shrink-0" />;
}

function MetricBlock({ label, value, accent = "text-slate-50" }) {
  return (
    <div className="rounded-lg p-2 bg-black/30 border border-white/5">
      <p className="text-[9px] uppercase tracking-wider text-slate-400 font-bold mb-0.5">
        {label}
      </p>
      <p className={`text-sm font-bold tracking-tight truncate ${accent}`}>
        {value}
      </p>
    </div>
  );
}

function CompactToggle({ active, onChange }) {
  return (
    <button
      onClick={onChange}
      title={active ? "Desativar" : "Ativar"}
      className="flex-shrink-0 inline-flex items-center gap-1.5"
    >
      <span
        className={`relative inline-flex items-center h-5 w-9 rounded-full transition-colors ${
          active ? "bg-green-500" : "bg-slate-800"
        }`}
      >
        <span
          className={`inline-block h-4 w-4 rounded-full bg-white shadow transform transition-transform ${
            active ? "translate-x-[18px]" : "translate-x-0.5"
          }`}
        />
      </span>
    </button>
  );
}

/* ========================================================================= */
/* CAMPAIGN CARD — cards de campanhas customizadas                           */
/* ========================================================================= */

function CampaignCard({ campaign, onToggle, onReport, onEditMessages }) {
  const meta = CAMPAIGN_TYPES[campaign.type] || {
    icon: Megaphone,
    description: "",
  };
  const Icon = meta.icon;
  const triggered = Number(campaign.triggered_count || 0);
  const converted = Number(campaign.converted_count || 0);
  const revenue = Number(campaign.recovered_revenue || 0);
  const isActive = !!campaign.is_active;

  return (
    <div
      className={`group relative bg-card rounded-2xl border overflow-hidden transition-all hover:shadow-lg hover:-translate-y-0.5 ${
        !isActive ? "opacity-70" : ""
      }`}
    >
      <div className="p-4">
        <div className="flex items-start justify-between gap-2 mb-3">
          <div className="flex items-start gap-3 min-w-0 flex-1">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-fuchsia-600 flex items-center justify-center shadow-md shadow-purple-500/30 flex-shrink-0">
              <Icon className="w-5 h-5 text-white" />
            </div>
            <div className="min-w-0">
              <h3 className="font-semibold text-sm truncate">{campaign.name}</h3>
              {campaign.description && (
                <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">
                  {campaign.description}
                </p>
              )}
            </div>
          </div>
          <StatusDot active={isActive} />
        </div>

        <div className="grid grid-cols-3 gap-2 mb-3">
          <MiniStat label="Disparos" value={compactNumber(triggered)} />
          <MiniStat label="Conversões" value={compactNumber(converted)} />
          <MiniStat label="Receita" value={formatCurrency(revenue)} />
        </div>
      </div>

      <div className="flex items-center justify-between px-4 py-2.5 border-t bg-muted/20">
        <button
          onClick={onToggle}
          className={`text-xs font-semibold transition-colors ${
            isActive
              ? "text-green-500 hover:text-green-600"
              : "text-muted-foreground hover:text-foreground"
          }`}
        >
          {isActive ? "Desativar" : "Ativar"}
        </button>
        <div className="flex gap-1">
          <IconButton onClick={onReport} title="Ver relatório">
            <BarChart3 className="w-3.5 h-3.5" />
          </IconButton>
          <IconButton onClick={onEditMessages} title="Editar mensagens">
            <Pencil className="w-3.5 h-3.5" />
          </IconButton>
        </div>
      </div>
    </div>
  );
}

function StatusDot({ active }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider">
      <span className="relative flex h-2 w-2">
        {active && (
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
        )}
        <span
          className={`relative inline-flex rounded-full h-2 w-2 ${
            active ? "bg-green-500" : "bg-muted-foreground/40"
          }`}
        />
      </span>
      <span className={active ? "text-green-500" : "text-muted-foreground"}>
        {active ? "Ativa" : "Inativa"}
      </span>
    </span>
  );
}

function MiniStat({ label, value }) {
  return (
    <div className="bg-muted/40 rounded-lg px-2 py-1.5">
      <p className="text-[9px] uppercase tracking-wider text-muted-foreground font-semibold">
        {label}
      </p>
      <p className="text-sm font-bold truncate">{value}</p>
    </div>
  );
}

function IconButton({ children, onClick, title }) {
  return (
    <button
      onClick={onClick}
      title={title}
      className="h-7 w-7 inline-flex items-center justify-center rounded-lg hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
    >
      {children}
    </button>
  );
}

/* ========================================================================= */
/* EMPTY STATE                                                               */
/* ========================================================================= */


/* ========================================================================= */
/* REVIEW CAMPAIGN — Card padronizado (igual aos outros) + Modal de config   */
/* ========================================================================= */

function ReviewCampaignSection() {
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState(null);
  // Só a primeira carga mostra o card piscando; salvar a configuração recarrega sem sumir com o card.
  const [primeiraCarga, setPrimeiraCarga] = useState(true);
  const [stats, setStats] = useState({ total: 0, avg_geral: null, dist: {} });
  const [configOpen, setConfigOpen] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      const [campRes, statsRes] = await Promise.all([
        axios.get(`${API}/campaigns/review`, getAuthHeader()),
        axios.get(`${API}/reports/reviews?days=90`, getAuthHeader()).catch(() => ({ data: { total: 0, avg_geral: null, distribuicao: {} } })),
      ]);
      setCampaign(campRes.data || {});
      setStats({
        total: statsRes.data?.total || 0,
        sent: statsRes.data?.sent || 0,
        avg_geral: statsRes.data?.avg_geral,
        dist: statsRes.data?.distribuicao || {},
      });
    } catch (e) {
      toast.error("Não consegui carregar a campanha de avaliação");
    } finally {
      setPrimeiraCarga(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleToggle = async () => {
    if (!campaign) return;
    const newEnabled = !campaign.enabled;
    setCampaign({ ...campaign, enabled: newEnabled });
    try {
      await axios.put(`${API}/campaigns/review`, { enabled: newEnabled }, getAuthHeader());
      toast.success(newEnabled ? "Campanha de avaliação ativada" : "Campanha desativada");
    } catch (e) {
      setCampaign({ ...campaign, enabled: !newEnabled });
      toast.error(e.response?.data?.detail || "Erro ao atualizar");
    }
  };

  if (primeiraCarga) return <CardsCampanhaCarregando quantos={1} soltoNaGrade />;

  const isActive = !!campaign?.enabled;
  const total = stats.total || 0;          // avaliações respondidas (cliente clicou + submitted_at)
  const sent = stats.sent || 0;            // mensagens efetivamente entregues no WhatsApp (reviews.status sent/opened/submitted)
  const avg = stats.avg_geral;
  const responseRate = formatPct(total, sent);

  return (
    <>
      <div className={`relative rounded-2xl border border-amber-500/30 bg-[#2a1a0b] text-slate-50 overflow-hidden transition-all hover:border-amber-500/50 flex flex-col ${!isActive ? "opacity-90" : ""}`}>
        {/* Top stripe — amber pra diferenciar da purple (sistema) */}
        <div className="h-1 bg-amber-500/70" />

        <div className="p-4 flex flex-col flex-1">
          {/* Header */}
          <div className="flex items-start justify-between gap-2 mb-3">
            <div className="flex items-start gap-3 min-w-0 flex-1">
              <div className="relative flex-shrink-0">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-400 to-yellow-500 flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-white" strokeWidth={2.2} />
                </div>
                {isActive && (
                  <span className="absolute -top-0.5 -right-0.5 flex h-2.5 w-2.5">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
                    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500 border border-[#2a1a0b]" />
                  </span>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <h3 className="font-bold text-sm truncate">Avaliação de Pedidos</h3>
                  <span
                    className="inline-flex items-center gap-0.5 text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-amber-500/20 text-amber-300 border border-amber-500/30"
                    title="Campanha de sistema — não pode ser excluída"
                  >
                    <Lock className="w-2.5 h-2.5" /> SISTEMA
                  </span>
                </div>
                <p className="text-[11px] text-amber-300/80 font-medium mt-0.5 line-clamp-1">
                  Cliente vira fã quando se sente ouvido
                </p>
              </div>
            </div>
            <CompactToggle active={isActive} onChange={handleToggle} />
          </div>

          {/* Descrição */}
          <p className="text-xs text-slate-400 mb-3 line-clamp-2">
            30 min depois do pedido concluído, a CLEO convida o cliente a avaliar pelo WhatsApp. As notas aparecem em Relatório de Avaliações.
          </p>

          {/* Flow compacto */}
          <div className="mb-3 rounded-xl bg-black/30 border border-white/5 p-2.5">
            <p className="text-[9px] uppercase tracking-widest font-bold text-amber-400/80 mb-2">
              Como funciona
            </p>
            <div className="flex items-center gap-1">
              <CompactStep icon={ShoppingBag} label="Pedido" />
              <CompactArrow />
              <CompactStep icon={Clock} label="30 min" />
              <CompactArrow />
              <CompactStep icon={MessageCircle} label="Link" highlight />
              <CompactArrow />
              <CompactStep icon={Sparkles} label="Nota" />
            </div>
          </div>

          {/* Métricas 2x2 */}
          <div className="grid grid-cols-2 gap-2 mb-3">
            <MetricBlock
              label="Avaliações enviadas"
              value={compactNumber(sent)}
              accent="text-slate-50"
            />
            <MetricBlock
              label="Avaliações respondidas"
              value={compactNumber(total)}
              accent="text-green-400"
            />
            <MetricBlock
              label="Nota média"
              value={avg != null ? avg.toFixed(1).replace(".", ",") : "—"}
              accent="text-amber-400"
            />
            <MetricBlock
              label="% de resposta"
              value={responseRate}
              accent="text-purple-400"
            />
          </div>

          {/* Ações */}
          <div className="flex items-center gap-1.5 mt-auto">
            <button
              onClick={() => navigate("/admin/relatorios/avaliacoes")}
              className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10 text-xs font-semibold transition-colors"
            >
              <BarChart3 className="w-3.5 h-3.5" /> Relatório
            </button>
            <button
              onClick={() => setConfigOpen(true)}
              className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10 text-xs font-semibold transition-colors"
            >
              <Pencil className="w-3.5 h-3.5" /> Configurar
            </button>
          </div>
        </div>
      </div>

      {configOpen && (
        <ReviewCampaignConfigModal
          campaign={campaign}
          onClose={() => setConfigOpen(false)}
          onSaved={() => { setConfigOpen(false); fetchAll(); }}
        />
      )}
    </>
  );
}
